import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import SeoMeta from '../components/marketing/SeoMeta';
import seoConfig from '../seo/marketingPages.json';

const corePagePaths = new Set([
  '/',
  '/features',
  '/pricing',
  '/demo',
  '/philosophy',
  '/help',
  '/privacy-policy',
  '/terms-of-service',
]);

const allPages = seoConfig.pages || [];
const landingPages = allPages.filter((page) => page?.path && !corePagePaths.has(page.path));

function findPage(pathname) {
  return landingPages.find((page) => page.path === pathname) || null;
}

export function getMarketingLandingPages() {
  return landingPages;
}

export default function MarketingLandingPage() {
  const location = useLocation();
  const page = findPage(location.pathname);

  if (!page) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="marketing-root">
      <SeoMeta page={page} />
      <div className="container py-12 space-y-12">
        <section className="marketing-hero marketing-hero--compact">
          <div className="marketing-orb orb-one" />
          <div className="marketing-orb orb-two" />
          <div className="marketing-hero__layout">
            <div>
              <p className="marketing-kicker">{page.kicker}</p>
              <h1 className="marketing-heading text-4xl font-bold mb-3">{page.h1}</h1>
              <p className="marketing-lede">{page.lede}</p>
              <div className="marketing-cta-row">
                <Link to="/demo" className="button button-primary px-6 py-3">
                  Book a walkthrough
                </Link>
                <Link to="/pricing" className="button button-secondary px-6 py-3">
                  View pricing
                </Link>
              </div>
            </div>
            <div className="marketing-hero__panel">
              <span className="marketing-pill">What clubs use it for</span>
              <div className="marketing-steps">
                {(page.sections?.[0]?.bullets || []).map((text, idx) => (
                  <div key={text} className="marketing-step">
                    <div className="marketing-step__index">{idx + 1}</div>
                    <p className="text-gray-700">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="marketing-section">
          <div className="marketing-grid">
            {(page.sections || []).map((section) => (
              <Card key={section.heading} className="marketing-card">
                <h2 className="marketing-heading text-2xl font-semibold mb-3">{section.heading}</h2>
                <p className="text-gray-600">{section.body}</p>
                {section.bullets?.length ? (
                  <ul className="bullet-list" style={{ marginTop: '1rem' }}>
                    {section.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </Card>
            ))}
          </div>
        </section>

        {page.faqs?.length ? (
          <section className="marketing-section">
            <div className="marketing-section__header">
              <h2 className="marketing-heading text-3xl font-semibold mb-2">Common questions</h2>
            </div>
            <div className="marketing-section-grid">
              {page.faqs.map((faq) => (
                <div key={faq.question} className="marketing-card">
                  <h3 className="text-xl font-semibold mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="marketing-band text-center">
          <div>
            <h2 className="marketing-heading text-3xl font-semibold mb-2">See how it would work for your club</h2>
            <p className="marketing-lede">Book a short walkthrough tailored to your club type and current admin setup.</p>
          </div>
          <div className="flex justify-center gap-4">
            <Link to="/demo" className="button button-primary px-6 py-2">
              Book a demo
            </Link>
            <Link to="/features" className="button button-secondary px-6 py-2">
              Explore features
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
